import { LOCALES, type Locale, localePath, ogLocale } from '../i18n'

type Meta = Record<string, string>
type Link = Record<string, string>
type Script = { type: string; children: string }

export function seoHead(o: {
  origin: string
  locale: Locale
  path: string
  title: string
  description: string
  image?: string
  keywords?: readonly string[]
  jsonLd?: Record<string, unknown>
  noindex?: boolean
  type?: 'website' | 'article'
}): { meta: Meta[]; links: Link[]; scripts: Script[] } {
  const url = (locale: Locale) => o.origin + localePath(locale, o.path)
  const image = o.image ? new URL(o.image, o.origin).toString() : `${o.origin}/og-default.png`
  const meta: Meta[] = [
    { title: o.title },
    { name: 'description', content: o.description },
    { property: 'og:site_name', content: 'Focus Cafe' },
    { property: 'og:type', content: o.type ?? 'website' },
    { property: 'og:title', content: o.title },
    { property: 'og:description', content: o.description },
    { property: 'og:url', content: url(o.locale) },
    { property: 'og:image', content: image },
    { property: 'og:locale', content: ogLocale(o.locale) },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: o.title },
    { name: 'twitter:description', content: o.description },
    { name: 'twitter:image', content: image },
  ]
  if (o.keywords?.length) meta.push({ name: 'keywords', content: o.keywords.join(',') })
  if (o.noindex) meta.push({ name: 'robots', content: 'noindex' })
  const links: Link[] = [
    { rel: 'canonical', href: url(o.locale) },
    ...LOCALES.map((l) => ({ rel: 'alternate', hrefLang: l, href: url(l) })),
    { rel: 'alternate', hrefLang: 'x-default', href: url('ja') },
  ]
  // JSON-LD は script 要素に埋めるので、文字列中の < で要素が閉じないようにする
  const scripts: Script[] = o.jsonLd
    ? [{ type: 'application/ld+json', children: JSON.stringify(o.jsonLd).replace(/</g, '\\u003c') }]
    : []
  return { meta, links, scripts }
}
